/**
 * Due-date preview for the commitment form.
 *
 * A deliberate mirror of backend/src/lib/schedule.ts — same clamping rule,
 * same bounds. It exists so the form can show the real dates as you type
 * instead of round-tripping, and so "the 31st, monthly" can be SEEN behaving
 * correctly in February rather than explained in a tooltip.
 *
 * The server remains the authority: it regenerates the schedule on save. If
 * the two ever disagree, the server wins — this only ever renders a preview.
 */
import type { CommitmentKind, Frequency } from '@/lib/types';

const MONTH_STEP: Record<Exclude<Frequency, 'WEEKLY' | 'ONE_OFF'>, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  SEMIANNUAL: 6,
  ANNUAL: 12,
};

const MAX_INSTALMENTS = 600;
const PREVIEW_LIMIT = 240;

export interface PreviewSpec {
  frequency: Frequency;
  startDate: string;
  dayOfMonth?: number | null;
  totalInstalments?: number | null;
  endDate?: string | null;
}

export interface Preview {
  dates: string[];
  /** Null when the schedule is open-ended. */
  total: number | null;
  error: string | null;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * The n-th occurrence, computed from the start rather than by stepping a
 * cursor — stepping overflows (31 Jan + 1 month = 3 Mar) and then sticks.
 */
function occurrence(spec: PreviewSpec, n: number): string {
  const [sy, sm, sd] = spec.startDate.split('-').map(Number) as [number, number, number];

  if (spec.frequency === 'ONE_OFF') return spec.startDate;

  if (spec.frequency === 'WEEKLY') {
    const d = new Date(Date.UTC(sy, sm - 1, sd));
    d.setUTCDate(d.getUTCDate() + n * 7);
    return d.toISOString().slice(0, 10);
  }

  const step = MONTH_STEP[spec.frequency];
  const anchor = spec.dayOfMonth ?? sd;
  const total = sy * 12 + (sm - 1) + n * step;
  const year = Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12;
  return iso(year, month, Math.min(anchor, daysInMonth(year, month)));
}

export function previewDates(spec: PreviewSpec): Preview {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(spec.startDate)) {
    return { dates: [], total: null, error: null };
  }
  if (spec.dayOfMonth != null && (spec.dayOfMonth < 1 || spec.dayOfMonth > 31)) {
    return { dates: [], total: null, error: 'Денят от месеца трябва да е между 1 и 31' };
  }
  if (spec.totalInstalments != null && spec.totalInstalments > MAX_INSTALMENTS) {
    return { dates: [], total: null, error: `Най-много ${MAX_INSTALMENTS} вноски` };
  }
  if (spec.endDate && spec.endDate < spec.startDate) {
    return { dates: [], total: null, error: 'Крайната дата е преди първото плащане' };
  }

  if (spec.frequency === 'ONE_OFF') {
    return { dates: [spec.startDate], total: 1, error: null };
  }

  if (spec.totalInstalments != null && spec.totalInstalments > 0) {
    const n = Math.min(spec.totalInstalments, PREVIEW_LIMIT);
    const dates = Array.from({ length: n }, (_, i) => occurrence(spec, i));
    return { dates, total: spec.totalInstalments, error: null };
  }

  if (spec.endDate) {
    const dates: string[] = [];
    for (let i = 0; i < MAX_INSTALMENTS; i++) {
      const d = occurrence(spec, i);
      if (d > spec.endDate) break;
      dates.push(d);
    }
    if (dates.length === 0) {
      return { dates: [], total: 0, error: 'Периодът не съдържа нито едно плащане' };
    }
    return { dates, total: dates.length, error: null };
  }

  // Open-ended: show enough to make the pattern obvious, no total.
  return {
    dates: Array.from({ length: 6 }, (_, i) => occurrence(spec, i)),
    total: null,
    error: null,
  };
}

export const KIND_LABELS: Record<CommitmentKind, string> = {
  CREDIT: 'Кредит',
  LEASE: 'Лизинг',
  SUBSCRIPTION: 'Абонамент',
  INSURANCE: 'Застраховка',
  TAX: 'Данък / такса',
  SALARY: 'Заплата',
  OTHER: 'Друго',
};

export const FREQUENCY_LABELS: Record<Frequency, string> = {
  MONTHLY: 'Месечно',
  QUARTERLY: 'На 3 месеца',
  SEMIANNUAL: 'На 6 месеца',
  ANNUAL: 'Годишно',
  WEEKLY: 'Седмично',
  ONE_OFF: 'Еднократно',
};
